/** @jsxImportSource @emotion/react */
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { Box, Collapsible, Grid, Text } from 'grommet'
import { useCallback, useEffect } from 'react'

import { routes } from 'routes'
import { useAuth } from 'hooks/useAuth'
import useDeviceSize from 'hooks/useDevice'
import { useProvider } from 'hooks/useMoodPlot'
import Notification from 'components/Notification'
import AddMoodBox from 'components/group/plot/AddMoodBox'
import { EmotionsList } from 'components/group/emotionsList'
import EmotionDial from 'components/emotion-dial/EmotionDial'
import { BasicEmotion, Emotion, ExistingGroupWithTags } from 'db-tables'

import JournalBox from './plot/JournalBox'
import CopyUrlButton from './CopyUrlButton'
import SelectedEmotions from './plot/SelectedEmotions'
import ViewResultsButton from './nav/ViewResultsButton'
import EmotionsAutocomplete from './plot/EmotionsAutocomplete'
import BrowseEmotionsButton from './nav/BrowseEmotionsButton'

type Props = {
  group: ExistingGroupWithTags
  emotions: Emotion[] | BasicEmotion[]
  showResults?: boolean
  redirectToResults?: boolean
}

export default function PlotInGroup({
  group,
  emotions,
  showResults = false,
  redirectToResults = false,
}: Props) {
  const router = useRouter()
  const { user } = useAuth()
  const { t } = useTranslation()
  const { small } = useDeviceSize()
  const {
    state: { angle, moods, limited, emotionsLimit, existingMoods },
    toggleEmotionWord,
    resetWords,
  } = useProvider()

  useEffect(() => {
    resetWords()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id])

  const onNewMood = useCallback(
    ({ error }) => {
      if (error) {
        console.error(error)
        return
      }

      if (!user && !redirectToResults) {
        // Non-auth users do not see results
        return
      }

      router.push(routes.group.results(group.short_code, router.query), undefined, {
        shallow: true,
      })
    },
    [group.short_code, redirectToResults, router, user],
  )
  const isAlreadyPlotted = existingMoods >= emotionsLimit
  const disablePlotting = limited || isAlreadyPlotted

  if (group.archived_at) {
    return (
      <Box width="large" margin={{ top: 'medium' }}>
        <Notification fill="horizontal" margin={{ bottom: 'small' }} status="warning">
          {t('components.add-mood-button.group-archived')}
        </Notification>
      </Box>
    )
  }

  return (
    <Box width="large" margin={{ top: 'medium', bottom: 'large' }}>
      <Grid columns={{ count: small ? 1 : 2, size: 'auto' }} gap="large">
        <Box fill="horizontal" direction="column">
          <EmotionDial />
          <SelectedEmotions />
        </Box>
        <Box
          direction="column"
          gap={small ? 'large' : 'small'}
          margin={{ horizontal: small ? 'large' : undefined, bottom: 'large' }}>
          <Collapsible open={!disablePlotting}>
            <Box round="xsmall" gap="small">
              <Box pad={{ horizontal: 'medium' }}>
                <Text size="small" textAlign="center">
                  {t('components.emotions-list.select-emotion-helper')}
                </Text>
              </Box>
              <EmotionsAutocomplete onSelected={toggleEmotionWord} emotions={emotions} />
            </Box>
          </Collapsible>
          <Collapsible open={angle !== null && (!group.allow_journal || !disablePlotting)}>
            <EmotionsList
              disabled={disablePlotting}
              emotions={emotions}
              angle={angle}
              selectedMoods={moods}
              onSelected={toggleEmotionWord}
            />
          </Collapsible>
          {group.allow_journal && (
            <Collapsible open={moods.length > 0}>
              <JournalBox />
            </Collapsible>
          )}
          <Collapsible open={isAlreadyPlotted}>
            <Notification fill="horizontal" status="ok">
              {t('components.add-mood-button.already-plotted')}
            </Notification>
          </Collapsible>
          <Collapsible open={angle !== null && !isAlreadyPlotted}>
            <AddMoodBox group={group} onCreate={onNewMood} />
          </Collapsible>
          <Collapsible open={angle === null && !isAlreadyPlotted}>
            <Box margin={{ vertical: '4rem' }}>
              <BrowseEmotionsButton />
            </Box>
          </Collapsible>
          {/* Hide buttons that lead person to another page when plotting */}
          <Collapsible open={moods.length === 0}>
            <Box gap="small" align="center" direction={small ? 'column' : 'row'}>
              {showResults && (
                <ViewResultsButton
                  shortCode={group.short_code}
                  color={isAlreadyPlotted ? 'brand' : 'white'}
                />
              )}
              <CopyUrlButton shortCode={group.short_code} />
            </Box>
          </Collapsible>
        </Box>
      </Grid>
    </Box>
  )
}
